import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { useMutation } from '../../hooks/useApi';
import logger from '../../utils/logger';
import { useSettings } from '../../context/SettingsContext';
import { Eye, FileDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function RaportTop10() {
  const { getCurrencyCode } = useSettings();
  const currencyCode = getCurrencyCode();
  const { roli, location } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [top10, setTop10] = useState({});
  const [error, setError] = useState('');
  const [faqjaAktuale, setFaqjaAktuale] = useState(1);
  const perFaqe = 5;

  const { execute: fetchRaport, loading: loadingRaport } = useMutation(
    async ({ from, to }) => {
      return await apiService.get('/raport-top10', { params: { from, to } });
    },
    {
      showNotifications: false
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFaqjaAktuale(1);
    try {
      const data = await fetchRaport({ from, to });
      setTop10(data);
      setError('');
    } catch (err) {
      setError('Nuk mund të merret raporti.');
    }
  };

  const handleExportPdf = async () => {
    if (!from || !to) {
      setError('Ju lutem zgjidhni datat për PDF.');
      return;
    }

    try {
      const response = await apiService.get('/raport-top10/pdf', {
        params: { from, to },
        responseType: 'blob'
      });

      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (err) {
      logger.error('PDF export error:', err);
      setError('Dështoi shkarkimi i PDF-it.');
    }
  };

  const renderTabela = (tabela, title) => {
    const offset = (faqjaAktuale - 1) * perFaqe;
    const topFaqja = tabela.slice(offset, offset + perFaqe);
    const faqetTotale = Math.ceil(tabela.length / perFaqe);

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lokacioni: {title}</h3>

        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>Faqe {faqjaAktuale} nga {faqetTotale}</span>
          <div className="flex gap-1">
            <button onClick={() => setFaqjaAktuale(1)} disabled={faqjaAktuale === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale - 1)} disabled={faqjaAktuale === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale + 1)} disabled={faqjaAktuale === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqetTotale)} disabled={faqjaAktuale === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16} /></button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <div className="card-premium overflow-hidden p-0">
            <table className="table-premium">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Produkti</th>
                  <th className="text-right">Sasia</th>
                  <th className="text-right">Totali ({currencyCode})</th>
                </tr>
              </thead>
              <tbody>
                {topFaqja.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{offset + index + 1}</td>
                    <td className="font-medium">{item.emri}</td>
                    <td className="text-right font-semibold">{item.sasia}</td>
                    <td className="text-right text-green-600 dark:text-green-400 font-semibold">{Number(item.totali).toFixed(2)} {currencyCode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {topFaqja.map((item, index) => (
            <div key={item.id || index} className="card-premium p-4 space-y-1">
              <div><strong className="text-gray-700 dark:text-gray-300">#</strong>: {offset + index + 1}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Produkti:</strong> {item.emri}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Sasia:</strong> {item.sasia}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Totali:</strong> <span className="text-green-600 dark:text-green-400 font-semibold">{Number(item.totali).toFixed(2)} {currencyCode}</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">📊 Top 10 Produktet më të Shitura</h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Nga data</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input-premium"
            required
          />
        </div>
        <div>
          <label className="form-label">Deri më</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input-premium"
            required
          />
        </div>
        <button
          type="submit"
          className="btn-premium mt-auto flex items-center justify-center gap-2"
        >
          <Eye size={18} /> Shfaq
        </button>
      </form>

      {error && <p className="p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 font-medium">{error}</p>}

      {top10 && (
        <div className="space-y-10">
          <button
            onClick={handleExportPdf}
            className="btn-premium flex items-center gap-2"
          >
            <FileDown size={18} /> Shkarko PDF
          </button>

          {roli === 'admin' ? (
  Object.entries(top10).map(([lokacion, data], i) => renderTabela(data, lokacion))
) : (
  (() => {
    const emrat = Object.keys(top10);
    const lok = emrat.length > 0 ? emrat[0] : null;
    const data = lok ? top10[lok] : [];
    return renderTabela(data, lok || 'Lokacioni Im');
  })()
)}

        </div>
      )}
    </div>
  );
}

export default RaportTop10;
